<template>
  <loader v-if="loading" />
  <div v-else class="container services">
    <h1>Our Services</h1>
    <recommendations
      :recommendations="recommendations"
      :schedule-with-counselor="scheduleWithCounselor"
      :recommended-actions="recommendedActions"
      :selected-recommendation="selectedRecommendation"
      :has-appointment-booked="hasAppointmentBooked"
      :user-has-partner-college="userHasPartnerCollege"
      :number-of-free-sessions="numberOfFreeSessions"
      :selected-recommendation-name="selectedRecommendationName"
      :select-recommendation="selectRecommendation"
    />

    <div v-if="recommendations || hasAppointmentBooked" class="row option-row">
      <div class="col-12" :class="{'col-md-6': !userHasPartnerCollege}">
        <div class="dashboard-card">
          <h2>Individual Sessions</h2>
          <p class="count-meta">You have {{ numberOfFreeSessions }} session credits remaining.</p>
          <p>
            Schedule a confidential, one-on-one, 50-minute online therapy session with a licensed counselor that’s a good fit for you.
          </p>
          <router-link
            to="/counselors"
            tag="button"
            class="btn btn-primary"
          >
            View Counselors
          </router-link>
        </div>
      </div>

      <div v-if="!isFaculty" class="col-12 col-md-6">
        <div class="dashboard-card">
          <h2>Wellbeing Workshops</h2>
          <p class="count-meta">You have {{ numberOfFreeGroupSessions }} Wellbeing Workshop credits remaining.</p>
          <p>
            Join other users in an online group focused on a particular mental health topic or struggle area.
          </p>
          <router-link
            to="/workshops"
            tag="button"
            class="btn btn-primary"
          >
            View Workshops
          </router-link>
        </div>
      </div>
      <div v-if="!isFaculty && userHasPartnerCollege" class="col-12 col-md-6">
        <div class="dashboard-card">
          <h2>On-Campus Events</h2>
          <p class="count-meta">Free for users.</p>
          <p>
            Register and attend upcoming mental health and wellbeing events hosted by your school.
          </p>
          <router-link
            to="/events"
            tag="button"
            class="btn btn-primary"
          >
            View Events
          </router-link>
        </div>
      </div>
    </div>
    <send-message-modal ref="sendMessageModal" />
    <vue-accessible-modal>
      <template v-slot:backdrop />
    </vue-accessible-modal>
  </div>
</template>

<script>
import moment from 'moment';
import Loader from '@/components/Loader.vue';
import SendMessageModal from '@/components/modals/SendMessageModal.vue';
import Recommendations from '@/students/components/recommendations/Recommendations.vue';
import SessionRequestModal from '../students/session-request/SessionRequestModal.vue'
import UserClient from '@/api/UserClient';
import StudentClient from '@/api/StudentClient';
import RecommendedActionModel from '@/models/RecommendedActionModel';

export default {
  name: 'Services',
  components: {
    Loader,
    Recommendations,
    SendMessageModal,
  },
  data() {
    return {
      userClient: new UserClient(this.$http),
      studentClient: new StudentClient(this.$http),
      isFaculty: false,
      loading: true,
      college: this.$store.getters.currentUser.school,
      primaryCounselor: null,
      recommendations: null, // counselors
      recommendedActions: null,
      scheduledAppointments: null,
      pendingAppointments: null,
      selectedRecommendation: null,
      numberOfFreeSessions: 0,
      numberOfFreeGroupSessions: 0,
    };
  },
  computed: {
    hasAppointmentBooked() {
      return (this.scheduledAppointments && this.scheduledAppointments.length > 0) ||
        (this.pendingAppointments && this.pendingAppointments.length > 0);
    },
    userHasPartnerCollege() {
      return !!(this.college && this.college.id);
    },

    selectedRecommendationName() {
      if (this.recommendations && this.selectedRecommendation) {
        return this.selectedRecommendation.name;
      }
      return '';
    },
  },
  async mounted() {
    try {
    this.isFaculty = await this.$globals.schoolHasFeature(
      'users-are-faculty', 
      this.$store.getters.currentUser.school.id);

    await Promise.all([
      this.loadRecommendations(),
      this.loadCredits(),
      this.loadSessions()
    ]);
    } catch (ex) {
      console.error(ex);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async loadCredits() {
      this.numberOfFreeSessions = 0;
      this.numberOfFreeGroupSessions = 0;
      try {
        const response = await this.studentClient.getRemainingCredits();
        this.numberOfFreeSessions = response.individual_session_credits_remaining || 0;
        this.numberOfFreeGroupSessions = response.group_session_credits_remaining || 0;
      } catch { }
    },
    async loadSessions() {
      this.pendingAppointments = [];
      this.scheduledAppointments = [];

      try {
        const [
          scheduledAppointmentsResponse,
          pendingAppointmentsResponse]
          = await Promise.all([
            this.studentClient.getMyScheduledAppointments(),
            this.studentClient.getMyAppointmentRequests(),
          ]);

        this.pendingAppointments = (pendingAppointmentsResponse && pendingAppointmentsResponse.items) || [];
        this.scheduledAppointments = (scheduledAppointmentsResponse && scheduledAppointmentsResponse.items) || [];
      } catch (ex) { }
    },
    async loadRecommendations() {
      this.primaryCounselor = null;
      this.recommendations = [];
      this.recommendedActions = new RecommendedActionModel({
        recommended_action: 'free_individual_session',
        recommended_counselor: null,
      });

      try {
        const response = await this.studentClient.getPrimaryCounselor(
          this.$store.getters.currentUser.id
        );
        if (response && response.counselor) {
          this.primaryCounselor = {
            ...response.counselor,
            name: `${response.counselor.firstName} ${response.counselor.lastName}`,
          };
          this.recommendedActions.recommended_counselor = this.primaryCounselor;
          this.recommendations = [this.primaryCounselor];
          this.selectedRecommendation = this.primaryCounselor;
          return;
        }
      } catch (ex) { console.error(ex) }

      try {
        const response = await this.studentClient.getRecommendedCounselors(
          this.$store.getters.currentUser.id,
          3,
          moment().toISOString()
        );
        if (response && response.counselors && response.counselors.length) {
          this.recommendations = response.counselors.map(counselor => ({
            ...counselor,
            name: `${counselor.firstName} ${counselor.lastName}`
          }));
          this.selectedRecommendation = this.recommendations[0];
          return;
        }
      } catch (ex) { console.error(ex) }
    },
    selectRecommendation(model, goToView = false) {
      const recommendation = {
        id: model.id,
        userId: model.userId,
        name: `${model.firstName} ${model.lastName}`,
      }
      if (goToView || this.$globals.isMobile) {
        this.scheduleWithCounselor(recommendation);
        // this.$router.push({ name: 'counselor-schedule', params: { id: model.id } });
      } else {
        this.selectedRecommendation = recommendation;
      }
    },
    scheduleWithCounselor(counselor) {
      if (this.$store.getters.currentUser.num_pending_payments > 0) {
        return this.$store.commit('setGlobalAlert', {
          msg: 'To schedule a session you must first complete your pending payments.', error: true,
        });
      }

      this.$modal.show(
        SessionRequestModal,
        {
          props: { counselor },
          listeners: {
            onerror: (err) => {
              console.error(err);
            },
            onsendamessage: (counselor) => {
              this.$refs.sendMessageModal.show(counselor.userId);
            },
            onrequest: (requestResult) => {
              this.$gtag.event('appointment_request', {
                student_id: requestResult.user.id,
                counselor_id: requestResult.counselor_id,
                start_time: requestResult.start_time,
              });
              this.$router.push({ name: 'dashboard', params: { from: 'event-scheduled' } });
            }
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.services {
  h1 {
    margin-bottom: 45px;
  }

  .dashboard-card {
    @include default-card;

    h2 {
      font-size: 28px;
      margin-bottom: 0.5rem;
    }

    label {
      @include label;

      display: inline-block;
      margin-bottom: 15px;
    }

    .btn-secondary {
      background: $green;
      color: #1c3a58;
    }
  }

  .option-row {
    margin-top: 30px;
  }
}

.count-meta {
  font-style: italic;
}
</style>
